import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { TransactionalSolidButton, SecondaryGhostButton, SecondarySolidButton } from '@bibliocommons/base-buttons';
import FormattedHTMLMessage from 'app/components/shared/FormattedHTMLMessage';
import { ModalHeader, ModalContent, ModalFooter } from 'app/components/shared/Modal';
import Divider from 'app/components/bibs/shared/Divider';
import { eventShape } from 'app/shapes/events';
import EventsActions from 'app/actions/EventsActions';
import EventDetails from './EventDetails';

const WAITLIST = 'waitlist';

export default function ExistingRegistrationPrompt({
  contactValue,
  event,
  eventSeries,
  status,
  onClose,
  onCompleteRegistration
}) {
  const dispatch = useDispatch();

  function handleResendConfirmationEmail() {
    dispatch(
      EventsActions.resendConfirmationEmail({
        email: contactValue,
        eventId: event.get('id')
      })
    );
    onClose();
  }

  function renderWaitlistModal() {
    return (
      <>
        <ModalHeader
          title={<FormattedMessage id="events.registration.confirm" defaultMessage="Confirm your attendance" />}
        />
        <ModalContent>
          <FormattedHTMLMessage
            id="events.registration.existing.waitlist"
            defaultMessage="<p>You are already on the waitlist to attend this event.</p>"
          />
          <FormattedHTMLMessage
            id="events.registration.existing.send_email_message"
            defaultMessage="<p>If you didn't receive a confirmation email, you can have it sent to you again.</p>"
          />
          <FormattedHTMLMessage
            id="events.registration.existing.contact_library_message"
            defaultMessage="<p>If you want to make a change to your attendance, please contact the library for help.</p>"
          />
        </ModalContent>
        <ModalFooter
          primaryActionButtonText={
            <FormattedMessage id="resend_confirmation_email" defaultMessage="Resend confirmation email" />
          }
          primaryActionButtonTag={TransactionalSolidButton}
          secondaryActionButtonText={<FormattedMessage defaultMessage="Close" id="button_close" />}
          handlePrimaryAction={handleResendConfirmationEmail}
          handleSecondaryAction={onClose}
          dataKey="resend-email-confirmation"
        />
      </>
    );
  }

  function renderRegisteredModal() {
    return (
      <>
        <ModalHeader
          title={<FormattedMessage id="events.registration.already_registered" defaultMessage="Already registered" />}
        />
        <ModalContent>
          <div className="registered-message">
            <FormattedHTMLMessage
              id="events.registration.existing.registered"
              defaultMessage="<p>You are already registered to attend this event.</p>"
            />
          </div>
          <EventDetails event={event} eventSeries={eventSeries} />
          <div className="resend-section">
            <FormattedHTMLMessage
              id="events.registration.existing.send_email_message"
              defaultMessage="<p>If you didn't receive a confirmation email, you can have it sent to you again.</p>"
            />
            <SecondaryGhostButton dataKey="resend-email-inline" handleClick={handleResendConfirmationEmail}>
              <FormattedMessage id="resend_confirmation_email" defaultMessage="Resend confirmation email" />
            </SecondaryGhostButton>
          </div>
          <div className="change-attendance-message">
            <FormattedHTMLMessage
              id="events.registration.existing.contact_library_message"
              defaultMessage="<p>If you want to make a change to your attendance, please contact the library for help.</p>"
            />
          </div>
          <Divider />
          <div className="complete-registration-section">
            <FormattedHTMLMessage
              id="events.registration.complete_registration.message"
              defaultMessage="Or, if you wish to register for this event again."
            />
            <TransactionalSolidButton dataKey="complete-registration" handleClick={onCompleteRegistration}>
              <FormattedMessage id="complete_registration" defaultMessage="Complete registration" />
            </TransactionalSolidButton>
          </div>
        </ModalContent>
        <ModalFooter
          secondaryActionButtonText={<FormattedMessage defaultMessage="Close" id="button_close" />}
          secondaryActionButtonTag={SecondarySolidButton}
          renderPrimaryAction={() => null}
          dataKey="existing-registration-footer"
        />
      </>
    );
  }

  return status === WAITLIST ? renderWaitlistModal() : renderRegisteredModal();
}

ExistingRegistrationPrompt.propTypes = {
  contactValue: PropTypes.string.isRequired,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired,
  status: PropTypes.oneOf(['open', 'waitlist', 'closed']),
  onClose: PropTypes.func.isRequired,
  onCompleteRegistration: PropTypes.func
};
